import React, { useState, useEffect, useRef } from 'react';
import { getRequest } from '../services/apiService';
import { FileText} from 'lucide-react';

const GanttChart = ({ selectedProject, selectedShip }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedTasks, setExpandedTasks] = useState({});
  const [zoomLevel, setZoomLevel] = useState('year');
  const timelineRef = useRef(null);
  const chartRef = useRef(null); // Reference for PNG export
  const [scrollPosition, setScrollPosition] = useState(0);
  const [taskYears, setTaskYears] = useState([]);
  const [taskMonths, setTaskMonths] = useState([]);

  // Mock data for demonstration
 useEffect(() => {
    if (!selectedShip) return;
    
    const fetchCriticalPath = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const data = await getRequest(
          `/transaction/critical-path/${selectedShip}/`
        );
        
        // Transform API data to match component format
        const transformedTasks = data.critical_path.map(task => ({
          id: task.id,
          name: task.name,
          startDate: task.start_date,
          endDate: task.end_date,
          duration: task.duration,
          next: task.next,
        }));

        setTasks(transformedTasks);
        
        const initialExpandedState = {};
        transformedTasks.forEach(task => {
          initialExpandedState[task.id] = true;
        });
        setExpandedTasks(initialExpandedState);
        
        // Extract unique years from tasks
        const yearsSet = new Set();
        transformedTasks.forEach(task => {
          const startYear = new Date(task.startDate).getFullYear();
          const endYear = new Date(task.endDate).getFullYear();
          yearsSet.add(startYear);
          yearsSet.add(endYear);
        });
        
        // Convert to sorted array
        const uniqueYears = Array.from(yearsSet).sort((a, b) => a - b);
        setTaskYears(uniqueYears);
        
      } catch (err) {
        console.error('Error fetching critical path:', err);
        setError('Failed to load critical path data');
      } finally {
        setLoading(false);
      }
    };

    fetchCriticalPath();
  }, [selectedShip]);

  // Function to download CSV
  const downloadCSV = () => {
    const csvData = [
      ['Task ID', 'Task Name', 'Start Date', 'End Date', 'Duration (Days)', 'Is Critical', 'Next Tasks']
    ];

    tasks.forEach(task => {
      csvData.push([
        task.id,
        task.name,
        task.startDate,
        task.endDate,
        task.duration,
        task.isCritical ? 'Yes' : 'No',
        Array.isArray(task.next) ? task.next.join(', ') : (task.next || '')
      ]);
    });

    const csvContent = csvData.map(row => 
      row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(',')
    ).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Critical_path_${selectedShip || 'data'}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleExpand = (taskId) => {
    setExpandedTasks(prev => ({
      ...prev,
      [taskId]: !prev[taskId]
    }));
  };

  const toggleZoom = () => {
    setZoomLevel(prev => prev === 'year' ? 'month' : 'year');
  };

  // Handle timeline scroll - this will control all timeline elements
  const handleTimelineScroll = (e) => {
    const scrollLeft = e.target.scrollLeft;
    setScrollPosition(scrollLeft);
  };

  // Calculate date range for the entire project
  const getDateRange = () => {
    if (tasks.length === 0) return { minDate: new Date(), maxDate: new Date() };
    
    const dates = tasks.flatMap(task => [
      new Date(task.startDate), 
      new Date(task.endDate)
    ]);
    
    const minDate = new Date(Math.min(...dates.map(date => date.getTime())));
    const maxDate = new Date(Math.max(...dates.map(date => date.getTime())));
    
    // Add some padding
    minDate.setMonth(minDate.getMonth() - 1, 1); // Start from beginning of previous month
    maxDate.setMonth(maxDate.getMonth() + 2, 0); // End at last day of next month
    
    return { minDate, maxDate };
  };

  const { minDate, maxDate } = getDateRange();

  // Get all months in the date range
  const getMonthsInRange = () => {
    const months = [];
    const current = new Date(minDate);
    
    while (current <= maxDate) {
      months.push({
        year: current.getFullYear(),
        month: current.getMonth(),
        date: new Date(current),
        monthName: current.toLocaleDateString('en-US', { month: 'short' }),
        yearName: current.getFullYear().toString()
      });
      current.setMonth(current.getMonth() + 1);
    }
    
    return months;
  };

  const months = getMonthsInRange();

  // Calculate timeline width based on zoom level
  const getTimelineWidth = () => {
    if (zoomLevel === 'month') {
      // In month mode, each month gets more space
      return Math.max(months.length * 120, 2000);
    } else {
      // In year mode, calculate based on relevant years
      return Math.max(taskYears.length * 300, 1200);
    }
  };

  const timelineWidth = getTimelineWidth();

  // Get position and width for task bars
  const getTaskPosition = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // For year view - use unique years instead of continuous months
    if (zoomLevel === 'year') {
      const startYear = start.getFullYear();
      const endYear = end.getFullYear();
      
      const startIndex = taskYears.indexOf(startYear);
      const endIndex = taskYears.indexOf(endYear);
      
      if (startIndex === -1 || endIndex === -1) {
        return { left: 0, width: 0, visible: false };
      }
      
      const segmentWidth = timelineWidth / taskYears.length;
      const left = startIndex * segmentWidth;
      const width = (endIndex - startIndex + 1) * segmentWidth;
      
      return { left, width, visible: true };
    }
    
    // For month view - original continuous month calculation
    let startMonthIndex = -1;
    let endMonthIndex = -1;
    
    // Find which months the task spans
    months.forEach((month, index) => {
      const monthStart = new Date(month.year, month.month, 1);
      const monthEnd = new Date(month.year, month.month + 1, 0);
      
      if (start >= monthStart && start <= monthEnd && startMonthIndex === -1) {
        // Calculate precise position within the month
        const dayOfMonth = start.getDate();
        const daysInMonth = monthEnd.getDate();
        const monthProgress = (dayOfMonth - 1) / daysInMonth;
        startMonthIndex = index + monthProgress;
      }
      if (end >= monthStart && end <= monthEnd) {
        // Calculate precise position within the month
        const dayOfMonth = end.getDate();
        const daysInMonth = monthEnd.getDate();
        const monthProgress = dayOfMonth / daysInMonth;
        endMonthIndex = index + monthProgress;
      }
    });
    
    // Handle cases where task starts before or ends after our range
    if (startMonthIndex === -1) {
      if (start < new Date(months[0].year, months[0].month, 1)) {
        startMonthIndex = 0;
      }
    }
    if (endMonthIndex === -1) {
      if (end > new Date(months[months.length - 1].year, months[months.length - 1].month + 1, 0)) {
        endMonthIndex = months.length - 1;
      }
    }
    
    if (startMonthIndex === -1 || endMonthIndex === -1) {
      return { left: 0, width: 0, visible: false };
    }
    
    // Calculate position in pixels
    const left = (startMonthIndex / months.length) * timelineWidth;
    const width = ((endMonthIndex - startMonthIndex) / months.length) * timelineWidth;
    
    return { left: Math.max(left, 0), width: Math.max(width, 40), visible: true };
  };

  const getYearHeaders = () => {
    return taskYears.map(year => {
      // Calculate width for each year segment
      const yearWidth = timelineWidth / taskYears.length;
      
      return (
        <div 
          key={year}
          className="flex items-center justify-center text-sm font-medium bg-gray-50 border-r border-gray-300 cursor-pointer hover:bg-gray-100 transition-colors h-16"
          style={{ width: `${yearWidth}px` }}
          onClick={toggleZoom}
          title="Click to zoom to months"
        >
          {year}
        </div>
      );
    });
  };

  const getMonthHeaders = () => {
    return months.map((month, index) => (
      <div 
        key={`${month.year}-${month.month}`}
        className="flex flex-col items-center justify-center text-xs font-medium bg-gray-50 border-r border-gray-300 cursor-pointer hover:bg-gray-100 transition-colors h-16"
        style={{ width: `${timelineWidth / months.length}px` }}
        onClick={toggleZoom}
        title="Click to zoom to years"
      >
        <div className="text-gray-600">{month.yearName}</div>
        <div className="text-gray-800 font-semibold">{month.monthName}</div>
      </div>
    ));
  };
  

  const renderTaskRow = (task) => {
    const hasChildren = tasks.some(t => t.parent === task.id);
    const isExpanded = expandedTasks[task.id];
    const { left, width, visible } = getTaskPosition(task.startDate, task.endDate);
    const isVeryShortTask = width < 20;

    return (
      <div key={task.id} className="group">
        <div className="flex h-12 border-b border-gray-200 hover:bg-gray-50 transition-colors">
          {/* Fixed Task Information Area */}
          <div className="flex bg-white border-r-2 border-gray-400 flex-shrink-0" style={{ width: '240px' }}>
            {/* Task Name */}
            <div className="flex-1 px-3 text-sm flex items-center">
              <span className={`truncate ${hasChildren ? 'font-semibold text-gray-800' : 'text-gray-700'} ${task.parent ? 'ml-4' : ''}`}>
                {hasChildren && (
                  <button
                    onClick={() => toggleExpand(task.id)}
                    className="w-4 h-4 inline-flex items-center justify-center text-gray-600 hover:bg-gray-200 rounded transition-colors mr-2"
                  >
                    {isExpanded ? '▼' : '▶'}
                  </button>
                )}
                {task.name}
              </span>
            </div>
          </div>
          
          {/* Timeline Area - No individual scrolling, positioned using transform */}
          <div className="flex-1 relative h-12 overflow-hidden">
            <div 
              className="relative h-full"
              style={{ 
                width: `${timelineWidth}px`,
                transform: `translateX(-${scrollPosition}px)`
              }}
            >
              {/* Background grid */}
              <div className="absolute inset-0 flex">
                {zoomLevel === 'year' ? (
                  // Year view grid - based on relevant years
                  taskYears.map((year, index) => (
                    <div 
                      key={year}
                      className="border-r border-gray-100 flex-shrink-0"
                      style={{ width: `${timelineWidth / taskYears.length}px` }}
                    ></div>
                  ))
                ) : (
                  // Month view grid - continuous months
                  months.map((month, index) => (
                    <div 
                      key={`${month.year}-${month.month}`}
                      className="border-r border-gray-100 flex-shrink-0"
                      style={{ width: `${timelineWidth / months.length}px` }}
                    ></div>
                  ))
                )}
              </div>
              
              {/* Task Bar */}
              {visible && width > 0 && (
                <div
                  className={`absolute top-3 h-6 rounded-md flex items-center px-2 ${
                    task.isCritical 
                      ? 'bg-gradient-to-r from-red-500 to-red-600' 
                      : 'bg-gradient-to-r from-blue-500 to-blue-600'
                  } shadow-sm group-hover:shadow-md transition-all text-white`}
                  style={{
                    left: `${left}px`,
                    width: `${Math.max(width, 20)}px`
                  }}
                  title={`${task.name}\nStart: ${new Date(task.startDate).toLocaleDateString()}\nEnd: ${new Date(task.endDate).toLocaleDateString()}\nDuration: ${task.duration} days`}
                >
                  {!isVeryShortTask && (
                    <span className="text-xs font-medium truncate">
                      {task.duration}d
                    </span>
                  )}
                  {isVeryShortTask && (
                    <div className="w-2 h-2 bg-white rounded-full opacity-80"></div>
                  )}
                </div>
              )}
              
              {/* Task start/end indicators for very short tasks */}
              {visible && isVeryShortTask && (
                <>
                  <div 
                    className="absolute top-2 w-0.5 h-8 bg-red-600 opacity-70"
                    style={{ left: `${left}px` }}
                    title={`Start: ${new Date(task.startDate).toLocaleDateString()}`}
                  ></div>
                  <div 
                    className="absolute top-2 w-0.5 h-8 bg-red-600 opacity-70"
                    style={{ left: `${left + width}px` }}
                    title={`End: ${new Date(task.endDate).toLocaleDateString()}`}
                  ></div>
                </>
              )}
            </div>
          </div>
        </div>
        
        {/* Render child tasks */}
        {hasChildren && isExpanded && 
          tasks
            .filter(t => t.parent === task.id)
            .map(childTask => renderTaskRow(childTask))
        }
      </div>
    );
  };

  const parentTasks = tasks.filter(task => !task.parent);

  if (loading) {
    return (
      <div className="w-full bg-white border border-gray-300 rounded-lg overflow-hidden p-6 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
        Loading critical path...
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full bg-white border border-gray-300 rounded-lg overflow-hidden p-6 text-center">
        <div className="text-red-500 text-lg mb-2">⚠️ Error</div>
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div className="w-full bg-white border border-gray-300 rounded-lg overflow-hidden p-6 text-center text-gray-500">
        No critical path data available
      </div>
    );
  }

  return (
    <div className="w-full bg-white border border-gray-300 rounded-lg overflow-hidden shadow-lg" ref={chartRef}>
      {/* Header */}
      <div className="bg-white border-b border-gray-300 p-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center flex-wrap gap-2">
            <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
              {tasks.length} Critical Tasks
            </span>
            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
              {zoomLevel === 'month' ? 'Month View' : 'Year View'}
            </span>
          </div>
          <div className="flex items-center gap-2">
             <div className="text-xs text-gray-600">
              <span className="font-medium">Duration:</span> {
                (() => {
                  const totalDays = Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24));
                  const years = Math.floor(totalDays / 365);
                  const months = Math.floor((totalDays % 365) / 30);
                  const days = totalDays % 30;
                  return `${years > 0 ? years + 'y ' : ''}${months > 0 ? months + 'm ' : ''}${days}d`;
                })()
              }
            </div>
            <button
              onClick={downloadCSV}
              className="px-3 py-1 bg-gray-300 text-gray-700 rounded hover:bg-gray-500 flex items-center space-x-1 text-sm shadow-sm"
              title="Download CSV"
            >
              <FileText size={16} className="mr-1" />
              CSV
            </button>
           
          </div>
        </div>
      </div>

      {/* Gantt Header with Main Scroll Control */}
      <div className="flex border-b border-gray-300 bg-gray-50">
        {/* Fixed Left Headers */}
        <div className="flex bg-white border-r-2 border-gray-400 flex-shrink-0" style={{ width: '240px' }}>
          <div className="flex-1 h-16 flex items-center px-3 text-xs font-medium text-gray-500">
            Task Name
          </div>
        </div>
        
        {/* Scrollable Timeline Headers - This controls the main scroll */}
        <div className="flex-1 overflow-x-auto" onScroll={handleTimelineScroll}>
          <div 
            className="flex"
            style={{ width: `${timelineWidth}px` }}
          >
            {zoomLevel === 'year' ? getYearHeaders() : getMonthHeaders()}
          </div>
        </div>
      </div>

      {/* Task Rows */}
      <div className="max-h-96 overflow-y-auto">
        <div>
          {parentTasks.map(task => renderTaskRow(task))}
        </div>
      </div>
      
      {/* Footer */}
      <div className="p-3 text-xs text-gray-500 text-center border-t border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <span>💡 Click on timeline headers to toggle between year/month view</span>
          <div className="flex items-center space-x-4 flex-wrap">
            <span className="text-red-600">● Critical Path Tasks</span>
            <span className="text-blue-600">↔ Scroll timeline above to navigate</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GanttChart;